/**
 * Milestone 1 — the walking skeleton.
 *
 * One page, read-only: what the machine has, and what is installed on Ollama.
 * The buttons that change something arrive at M2.
 *
 * The shell (sidebar, routes) is here from the start so later milestones add a
 * screen rather than restructuring the app.
 */
import { NavLink, Route, Routes } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Fragment, useState, type FormEvent } from "react";

import {
  bytes,
  cancelJob,
  count,
  expiresIn,
  fetchHardwareAll,
  fetchJobs,
  fetchOllamaModels,
  fetchConnections,
  fetchNodes,
  fetchOllamaRunning,
  fetchSession,
  fetchStatus,
  installModel,
  isNoteworthy,
  loadModel,
  mib,
  removeModel,
  signOut,
  unloadModel,
  type Connection,
  type Job,
  type Model,
  type NodeHardware,
  type RunningModel,
} from "./api";
import { ApiError } from "./api";
import { useLive } from "./live";
import { ConnectionDetails, useKey } from "./Connection";
import ConfigureDialog from "./ConfigureDialog";
import ConnectionsPage from "./ConnectionsPage";
import NodesPage from "./NodesPage";
import VllmPage from "./VllmPage";
import LoginPage from "./LoginPage";

/** Every button goes through here: fire the call, then refresh the lists so the
 *  table reflects reality rather than what we hoped would happen. */
function useAction() {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setProblem(null),
    onError: (error) =>
      setProblem(error instanceof ApiError ? error.detail : String(error)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      void queryClient.invalidateQueries({ queryKey: ["ollama"] });
    },
  });

  return { run, problem, clearProblem: () => setProblem(null) };
}


/**
 * Light, dark, or whatever the machine says.
 *
 * Three states rather than two, because "follow the system" is the honest
 * default — a console that ignores the OS setting is one people re-toggle every
 * morning. An explicit choice stamps data-theme on the root; System removes it
 * and lets prefers-color-scheme decide.
 *
 * The key is shared with the AginticsHub admin console, so a choice made in one
 * is a choice made in both. index.html reads it before first paint, which is
 * what stops the white flash on a dark-mode machine.
 */
const THEME_KEY = "aghub-theme";
type Theme = "system" | "light" | "dark";

function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === "dark" || stored === "light" ? stored : "system";
  } catch {
    return "system";
  }
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readTheme);

  const apply = (next: Theme) => {
    setTheme(next);
    const root = document.documentElement;
    if (next === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", next);
    try {
      if (next === "system") localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, next);
    } catch {
      // Private mode. The attribute is already set, so this session is fine;
      // only remembering it across reloads is lost, and that is not worth an
      // error message.
    }
  };

  const order: Theme[] = ["system", "light", "dark"];
  const label = { system: "Theme: system", light: "Theme: light", dark: "Theme: dark" };

  return (
    <button
      className="themebtn"
      type="button"
      onClick={() => apply(order[(order.indexOf(theme) + 1) % order.length])}
      title="Switch between system, light and dark"
    >
      {label[theme]}
    </button>
  );
}

/** The backend's version, in the corner where "milestone 5 · nodes" used to
 *  sit hard-coded — a label that was already two milestones stale. */
function PlatformVersion() {
  const { data } = useQuery({ queryKey: ["status"], queryFn: fetchStatus });
  return <span>{data ? `platform ${data.platform_version}` : ""}</span>;
}

function Sidebar({ modelCount }: { modelCount: number | undefined }) {
  const queryClient = useQueryClient();
  const out = useMutation({
    mutationFn: signOut,
    // Clear every cached answer, not just the session — otherwise the dashboard
    // stays on screen showing the last data it had after signing out.
    onSuccess: () => queryClient.clear(),
  });

  return (
    <aside>
      <div className="brand">
        <div className="mk">M</div>
        <div>
          <b>Model Serving</b>
          <span>models.os.agintics.ai</span>
        </div>
      </div>
      <nav className="side" aria-label="Sections">
        <p className="nav-label">Manage</p>
        {/* "Ollama", not "Models". It was named before vLLM had a page, and
            a section called Models that shows half of them is a section that
            misleads on its first line. */}
        <NavLink to="/" className={({ isActive }) => (isActive ? "on" : "")} end>
          Ollama
          {modelCount !== undefined && <span className="cnt">{modelCount}</span>}
        </NavLink>
        {/* Its own entry, not a section of Models. A vLLM model is a process
            with a port and a log; an Ollama model is a file that may be loaded.
            One table for both would be half-empty in both directions. */}
        <NavLink to="/vllm-models" className={({ isActive }) => (isActive ? "on" : "")}>
          vLLM
        </NavLink>
        <NavLink
          to="/connections"
          className={({ isActive }) => (isActive ? "on" : "")}
        >
          Connections
        </NavLink>
        <NavLink to="/nodes" className={({ isActive }) => (isActive ? "on" : "")}>
          Nodes
        </NavLink>
      </nav>
      <div className="side-foot">
        <ThemeToggle />
        <button
          className="side-signout"
          type="button"
          onClick={() => out.mutate()}
          disabled={out.isPending}
        >
          {out.isPending ? "Signing out…" : "Sign out"}
        </button>
        <PlatformVersion />
      </div>
    </aside>
  );
}

/** A failed fetch must say what went wrong and what to do. The M1 gate checks
 *  that killing the backend produces this rather than a blank page. */
function Problem({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null;
  const unreachable = api?.status === 0;
  return (
    <div className="notice">
      <h3>{unreachable ? "Cannot reach the server" : "The server returned an error"}</h3>
      <p>{api?.detail ?? String(error)}</p>
      {unreachable && (
        <p style={{ marginTop: 6 }}>
          Check that the backend is running, and that Caddy is forwarding{" "}
          <code>/api</code> to it.
        </p>
      )}
    </div>
  );
}

/**
 * Says whether a number on screen is keeping itself up to date.
 *
 * Worth the line. A reading that is refreshing and one that is frozen from
 * however long ago you opened the page look identical, and quietly showing a
 * stale VRAM figure is worse than showing none.
 */
function LiveHint({ which }: { which: "live_hardware" | "live_running" }) {
  const interval = useLive(which);
  return (
    <span className="hint">
      {interval
        ? `refreshes every ${Math.round(interval / 1000)} seconds`
        : "as of when this page loaded"}
    </span>
  );
}

/**
 * Hardware, one reading per machine.
 *
 * Drawn from /hardware/all rather than /hardware, because the latter reports
 * the box the API runs on. On a router that is a Mac with no GPU, and the card
 * used to say "no NVIDIA GPU detected" about it while the Windows box sat there
 * with a card to spare. On a single-machine install the list has one entry and
 * no heading, so the page looks exactly as it did.
 */
function useHardware() {
  return useQuery({
    queryKey: ["hardware", "all"],
    queryFn: fetchHardwareAll,
    // VRAM and temperature move on their own, so this is one of the two the
    // server can ask us to keep live. Off by default.
    refetchInterval: useLive("live_hardware"),
  });
}

function HardwareStrip() {
  const { data, error, isLoading } = useHardware();

  if (isLoading) return <div className="card"><div className="empty">Reading hardware…</div></div>;
  if (error) return <Problem error={error} />;
  if (!data) return null;

  return (
    <>
      {data.map((entry) => (
        <MachineStrip key={entry.node ?? "local"} entry={entry} />
      ))}
      {/* Whether these numbers are keeping themselves up to date. A reading
          that is refreshing and one frozen from when the page loaded look
          identical, and a stale VRAM figure is worse than none. */}
      <p className="striphint">
        <LiveHint which="live_hardware" />
      </p>
    </>
  );
}

function MachineStrip({ entry }: { entry: NodeHardware }) {
  const data = entry.hardware;
  // A machine that did not answer still gets its row, with its name and no
  // numbers. Dropping it would make "which box has room" unanswerable from a
  // page that exists to answer it.
  if (!data) {
    return (
      <div className="strip">
        <div>
          <p className="k">{entry.node ?? "This machine"}</p>
          <p className="v" style={{ fontSize: "1.05rem" }}>not answering</p>
          <p className="s">no reading</p>
        </div>
      </div>
    );
  }

  // Every card, not just the first. A machine with two was showing one and
  // saying nothing about the other — and "which box has room" is the question
  // this row exists to answer.
  const gpus = data.gpus;

  return (
    <>
      {entry.node && <p className="stripname">{entry.node}</p>}
      <div className="strip">
        {gpus.length === 0 ? (
          <div>
            <p className="k">GPU</p>
            <p className="v" style={{ fontSize: "1.05rem" }}>none</p>
            <p className="s">no NVIDIA GPU detected</p>
          </div>
        ) : (
          gpus.map((gpu, i) => (
            <Fragment key={gpu.index ?? i}>
              <div>
                <p className="k">{gpus.length > 1 ? `GPU ${i}` : "GPU"}</p>
                <p className="v" style={{ fontSize: "1.05rem" }}>
                  {gpu.name.replace(/^NVIDIA GeForce /, "")}
                </p>
                <p className="s">driver {gpu.driver_version ?? "?"}</p>
              </div>
              <div>
                <p className="k">{gpus.length > 1 ? `GPU ${i} memory` : "GPU memory"}</p>
                <p className="v">
                  {mib(gpu.vram_used_mib)}
                  <small>/ {mib(gpu.vram_total_mib)}</small>
                </p>
                <p className="s">{mib(gpu.vram_free_mib)} free</p>
              </div>
              <div>
                <p className="k">{gpus.length > 1 ? `GPU ${i} load` : "GPU load"}</p>
                <p className="v">
                  {gpu.utilization_pct ?? "—"}
                  <small>%</small>
                </p>
                <p className="s">
                  {gpu.temperature_c != null ? `${gpu.temperature_c} °C` : ""}
                </p>
              </div>
            </Fragment>
          ))
        )}
        <div>
          <p className="k">System RAM</p>
          <p className="v">
            {mib(data.ram_used_mib)}
            <small>/ {mib(data.ram_total_mib)}</small>
          </p>
          <p className="s">{data.cpu_logical ?? "?"} logical cores</p>
        </div>
        <div>
          <p className="k">Disk free</p>
          <p className="v">
            {data.disk_free_gib ?? "—"}
            <small>GB</small>
          </p>
          <p className="s">of {data.disk_total_gib ?? "—"} GB on {data.disk_path}</p>
        </div>
      </div>
    </>
  );
}


/**
 * Whether the two model runtimes are answering, and on what version.
 *
 * Not about models — about the software that serves them. It is the first
 * thing to look at when the console is up and nothing will start: "Ollama not
 * reachable" means the daemon is down on the machine, and no amount of
 * clicking Load will fix that.
 *
 * On a router each answer covers the fleet: up if any machine answers, with
 * the count underneath, because "is Ollama up" stops being a yes-or-no as
 * soon as there is more than one box.
 */
function StatusPanel() {
  const { data } = useQuery({ queryKey: ["status"], queryFn: fetchStatus });
  return (
    <div className="card">
      <header>
        <h2>Runtimes</h2>
        <div className="r">
          <span className="hint">the software that serves the models</span>
        </div>
      </header>
      <div className="card-body">
        <div className="facts">
          <div>
            <span className="k">Platform</span>
            <span className="v">{data?.platform_version ?? "—"}</span>
          </div>
          <div>
            <span className="k">Ollama</span>
            {/* A dev build's version is forty characters of git hash. The title
                keeps all of it for anyone who needs to quote it. */}
            <span className="v trunc" title={data?.ollama.version ?? undefined}>
              {data?.ollama.reachable ? data.ollama.version ?? "up" : "not reachable"}
            </span>
            {data?.ollama.url && <span className="s">{data.ollama.url}</span>}
          </div>
          <div>
            <span className="k">vLLM</span>
            <span className="v trunc" title={data?.vllm.version ?? undefined}>
              {data?.vllm.reachable ? data.vllm.version ?? "up" : "not installed"}
            </span>
            {data?.vllm.url && <span className="s">{data.vllm.url}</span>}
          </div>
        </div>
        {data && !data.ollama.reachable && (
          <p className="hint" style={{ marginTop: 10 }}>
            Tried <code>{data.ollama.url}</code>. {data.ollama.error}
          </p>
        )}
        {data && !data.vllm.reachable && data.vllm.error && (
          <p className="hint" style={{ marginTop: 10 }}>
            vLLM: {data.vllm.error}
          </p>
        )}
      </div>
    </div>
  );
}

function ModelsTable({
  models,
  running,
  jobs,
  connections,
  action,
  onConfigure,
}: {
  models: Model[];
  running: RunningModel[];
  jobs: Job[];
  connections: Connection[];
  action: ReturnType<typeof useAction>;
  onConfigure: (model: string, node: string | null) => void;
}) {
  // Keyed by machine as well as name: the same model can be loaded on one and
  // idle on another, and that is the difference the page exists to show.
  const key = (name: string, node: string | null | undefined) =>
    node ? `${node}/${name}` : name;
  const loaded = new Map(running.map((r) => [key(r.name, r.node), r]));
  const multiNode = models.some((m) => m.node);
  // A model with a job in flight gets buttons replaced by what is happening —
  // clicking Remove while a pull runs would just earn a 409.
  const busy = new Map(
    jobs.filter((j) => j.state === "running").map((j) => [key(j.model, j.node), j]),
  );
  const conns = new Map(connections.map((c) => [c.model, c]));
  // One row open at a time. Several open at once turns the table into a wall
  // and loses the thing that made it a table.
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th>Model</th>
            {multiNode && <th>Node</th>}
            <th>Provider</th>
            <th>Type</th>
            <th>State</th>
            <th className="right">Context</th>
            <th className="right">Size</th>
            <th>Note</th>
            <th className="right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => {
            const live = loaded.get(key(m.name, m.node));
            const job = busy.get(key(m.name, m.node));
            const conn = conns.get(m.name);
            // A model can be on several machines, so the name alone is not a
            // key — and neither is it what "open" means any more.
            const rowKey = m.node ? `${m.node}/${m.name}` : m.name;
            return (
              <Fragment key={rowKey}>
              <tr className={open === rowKey ? "opened" : undefined}>
                <td>
                  <div className="mname">
                    <b>{m.name}</b>
                    <span>
                      {[m.family, m.parameter_size, m.quantization]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </span>
                  </div>
                </td>
                {multiNode && (
                  <td>
                    <span className="chip node">{m.node ?? "—"}</span>
                  </td>
                )}
                <td>
                  <span className={`prov ${m.provider}`}>
                    {m.provider === "ollama" ? "Ollama" : "vLLM"}
                  </span>
                </td>
                <td>
                  <span className="chip">{m.model_type}</span>
                  {m.embedding_length && (
                    <span className="chip">{m.embedding_length} dims</span>
                  )}
                </td>
                <td>
                  {job ? (
                    <span className="pill busy">
                      <i />
                      {job.detail ?? job.kind}
                    </span>
                  ) : live ? (
                    <span className="pill loaded">
                      <i />
                      Loaded
                    </span>
                  ) : (
                    <span className="pill idle">
                      <i />
                      Installed
                    </span>
                  )}
                </td>
                {/* Two numbers, deliberately both. `context_length` is the
                    architecture's ceiling and is identical for a base model and
                    anything derived from it — showing only that made a model
                    configured to 32k read as untouched. */}
                <td className="right num">
                  {m.num_ctx ? (
                    <div className="ctx">
                      <b>{count(m.num_ctx)}</b>
                      {m.context_length && m.context_length !== m.num_ctx && (
                        <span>of {count(m.context_length)}</span>
                      )}
                    </div>
                  ) : (
                    count(m.context_length)
                  )}
                </td>
                <td className="right num">{bytes(m.size_bytes)}</td>
                <td className="hint">
                  {live
                    ? [
                        mib(live.vram_bytes ? live.vram_bytes / 1024 ** 2 : null),
                        expiresIn(live.expires_at),
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "not in memory"}
                </td>
                <td className="right">
                  <div className="rowacts">
                    {/* The thing people come here to do. It used to be a page
                        away, which is one click too many for "what do I paste
                        into the app". */}
                    {conn && (
                      <button
                        className={`btn sm ${open === rowKey ? "primary" : ""}`}
                        type="button"
                        onClick={() => setOpen(open === rowKey ? null : rowKey)}
                      >
                        {open === rowKey ? "Hide" : "Connect"}
                      </button>
                    )}
                    {job ? (
                      <span className="hint">working…</span>
                    ) : live ? (
                      <button
                        className="btn sm"
                        type="button"
                        disabled={action.run.isPending}
                        onClick={() => action.run.mutate(() => unloadModel(m.name, m.node))}
                      >
                        Unload
                      </button>
                    ) : (
                      <LoadControl model={m.name} node={m.node} action={action} />
                    )}
                    {/* Embedding models have nothing worth baking — no context
                        length, no generation defaults — so the button that
                        would open an empty form is not offered. */}
                    {m.model_type !== "embedding" && (
                      <button
                        className="btn sm"
                        type="button"
                        disabled={!!job || action.run.isPending}
                        onClick={() => onConfigure(m.name, m.node)}
                      >
                        Configure
                      </button>
                    )}
                    <button
                      className="btn sm danger"
                      type="button"
                      disabled={!!job || action.run.isPending}
                      onClick={() => {
                        // Removing a model deletes gigabytes and cannot be
                        // undone without downloading it again. Worth a pause.
                        if (confirm(`Remove ${m.name}? It will have to be downloaded again.`)) {
                          action.run.mutate(() => removeModel(m.name, m.node));
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
              {open === rowKey && conn && (
                <tr className="expand">
                  <td colSpan={multiNode ? 9 : 8}>
                    <ConnectPanel conn={conn} />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Its own component so `useKey` has somewhere to live — hooks cannot be called
 *  from inside the row loop. */
function ConnectPanel({ conn }: { conn: Connection }) {
  const keyState = useKey(conn);
  return (
    <div className="conninline">
      <ConnectionDetails conn={conn} keyState={keyState} />
    </div>
  );
}

/**
 * Load, with a choice of how long it stays.
 *
 * `keep_alive` cannot be baked into a model — Ollama accepts
 * `PARAMETER keep_alive` and then ignores it — so this is the only place it can
 * be set. The backend has taken it since M2; before this the button always sent
 * the 5-minute default and there was no way to ask for anything else.
 *
 * "Always" is the one worth thinking about: it holds the VRAM until something
 * evicts it, which on a single GPU means the next model may not fit.
 */
function LoadControl({
  model,
  node,
  action,
}: {
  model: string;
  node: string | null;
  action: ReturnType<typeof useAction>;
}) {
  const [keepAlive, setKeepAlive] = useState("5m");
  return (
    <span className="loadctl">
      <select
        value={keepAlive}
        onChange={(e) => setKeepAlive(e.target.value)}
        aria-label={`How long ${model} stays loaded`}
        title="How long it stays in the GPU after its last request"
      >
        <option value="5m">5 min</option>
        <option value="30m">30 min</option>
        <option value="-1">Always</option>
      </select>
      <button
        className="btn sm"
        type="button"
        disabled={action.run.isPending}
        onClick={() => action.run.mutate(() => loadModel(model, keepAlive, node))}
      >
        Load
      </button>
    </span>
  );
}

function InstallBox({
  action,
  nodes,
}: {
  action: ReturnType<typeof useAction>;
  /** The machines available to install on. Empty on a single-machine setup,
   *  where there is nothing to choose and the picker does not appear. */
  nodes: string[];
}) {
  const [name, setName] = useState("");
  const [node, setNode] = useState("");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const model = name.trim();
    if (!model || action.run.isPending) return;
    action.run.mutate(() => installModel(model, node || null), {
      onSuccess: () => setName(""),
    });
  }

  return (
    <form className="install" onSubmit={onSubmit}>
      <input
        type="text"
        value={name}
        placeholder="qwen3:8b, or hf.co/user/repo:Q4_K_M"
        aria-label="Model to install"
        onChange={(e) => setName(e.target.value)}
        disabled={action.run.isPending}
      />
      {/* Only when there is a choice to make. A router refuses an install that
          names no machine — deliberately, since where a model lands is a
          decision about hardware and only the operator knows which box they
          meant — so the picker starts empty and the button waits for it. */}
      {nodes.length > 0 && (
        <select
          value={node}
          onChange={(e) => setNode(e.target.value)}
          aria-label="Machine to install on"
          disabled={action.run.isPending}
        >
          <option value="">Which machine…</option>
          {nodes.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      )}
      <button
        className="btn primary"
        type="submit"
        disabled={
          !name.trim() || action.run.isPending || (nodes.length > 0 && !node)
        }
      >
        {action.run.isPending ? "Starting…" : "Install"}
      </button>
    </form>
  );
}

function JobList({ jobs, action }: { jobs: Job[]; action: ReturnType<typeof useAction> }) {
  const shown = jobs.filter(isNoteworthy);
  if (shown.length === 0) return null;

  return (
    <div className="card">
      <header>
        <h2>In progress</h2>
      </header>
      <div className="card-body" style={{ padding: 0 }}>
        {shown.map((job) => (
          <div className="jobrow" key={job.id}>
            <div className="jobmain">
              <b>
                {job.kind === "pull" ? "Installing" : job.kind} {job.model}
              </b>
              <span className="hint">
                {job.state === "failed" ? job.error : job.detail ?? "working"}
                {job.total && job.completed
                  ? ` · ${bytes(job.completed)} of ${bytes(job.total)}`
                  : ""}
              </span>
              {/* Only when there is a real number behind it. A download
                  reports bytes; a load, a start or a vLLM launch reports
                  nothing, and drawing a bar for those left one sitting at zero
                  looking broken for the whole minute it took. The detail line
                  above carries the progress for those — it is the runtime's own
                  words, which is more than a stripe ever said. */}
              {job.state === "running" && job.percent != null && (
                <div className="prog">
                  <i style={{ width: `${job.percent}%` }} />
                </div>
              )}
            </div>
            <div className="jobside">
              {job.state === "running" ? (
                <button
                  className="btn sm"
                  type="button"
                  onClick={() => action.run.mutate(() => cancelJob(job.id))}
                >
                  Cancel
                </button>
              ) : (
                <span className="pill fail">
                  <i />
                  Failed
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModelsPage() {
  const models = useQuery({ queryKey: ["ollama", "models"], queryFn: fetchOllamaModels });
  // The other thing that changes unattended: a keep-alive expires and a model
  // drops out of memory with nobody watching.
  const running = useQuery({
    queryKey: ["ollama", "running"],
    queryFn: fetchOllamaRunning,
    refetchInterval: useLive("live_running"),
  });
  const action = useAction();
  const [configuring, setConfiguring] =
    useState<{ model: string; node: string | null } | null>(null);
  // Masked by default. The key is only fetched in the clear when someone asks
  // for it, so it does not sit in memory on a page nobody is reading closely.
  const connections = useQuery({
    queryKey: ["connections"],
    queryFn: () => fetchConnections(false),
  });
  // A single-machine install answers 400 here, which is the honest signal that
  // there is nothing to choose between. Failure is expected, so it is not shown
  // — the picker simply does not appear.
  const nodes = useQuery({
    queryKey: ["nodes"],
    queryFn: fetchNodes,
    retry: false,
  });
  const nodeNames = (nodes.data ?? []).filter((n) => n.enabled).map((n) => n.name);

  const jobs = useQuery({
    queryKey: ["jobs"],
    queryFn: fetchJobs,
    // The one poll that is always on, and only while there is something to
    // watch: a download's progress bar has to move. With nothing running it
    // stops entirely rather than idling at five seconds.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((j) => j.state === "running") ? 1000 : false,
  });
  const jobList = jobs.data ?? [];

  return (
    <main>
      <div className="page-head">
        <div>
          <h1>Ollama</h1>
          <p>Everything installed on Ollama, on every machine. Loaded on demand.</p>
        </div>
        <div className="acts">
          <button
            className="btn"
            type="button"
            onClick={() => {
              void models.refetch();
              void running.refetch();
              void jobs.refetch();
            }}
            disabled={models.isFetching}
          >
            {models.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {action.problem && (
        <div className="notice">
          <h3>That did not work</h3>
          <p>{action.problem}</p>
        </div>
      )}

      <HardwareStrip />

      <JobList jobs={jobList} action={action} />

      {/* Full width. The table has eight columns and gets clipped in a
          two-column layout, which is worse than a longer page. */}
      <div className="card">
        <header>
          <h2>Installed models</h2>
          <div className="r">
            <span className="hint">
              {models.data ? `${models.data.length} installed` : ""}
            </span>
          </div>
        </header>
        {models.error ? (
          <div className="card-body">
            <Problem error={models.error} />
          </div>
        ) : models.isLoading ? (
          <div className="empty">Loading models…</div>
        ) : models.data && models.data.length > 0 ? (
          <ModelsTable
            models={models.data}
            running={running.data ?? []}
            jobs={jobList}
            connections={connections.data ?? []}
            action={action}
            onConfigure={(model, node) => setConfiguring({ model, node })}
          />
        ) : (
          <div className="empty">Nothing installed yet. Add one below.</div>
        )}
        <div className="card-foot">
          <InstallBox action={action} nodes={nodeNames} />
          <p className="hint">
            A name from the Ollama library, or a Hugging Face GGUF repo. The
            download keeps going if you close this page.
          </p>
        </div>
      </div>

      <StatusPanel />

      {configuring && (
        <ConfigureDialog
          model={configuring.model}
          node={configuring.node}
          onClose={() => setConfiguring(null)}
        />
      )}
    </main>
  );
}

export default function App() {
  // The gate. Everything else in this app assumes a session exists, so nothing
  // renders until this answers. It is refetched on window focus like the rest,
  // so a session expiring while a tab sits open drops back to the login screen
  // rather than filling the page with 401s.
  const session = useQuery({ queryKey: ["session"], queryFn: fetchSession });

  const models = useQuery({
    queryKey: ["ollama", "models"],
    queryFn: fetchOllamaModels,
    enabled: session.data?.signed_in === true,
  });

  if (session.isLoading) {
    return <div className="login-shell" />;
  }

  // A network failure reaching /api/auth/me means the backend is down, which is
  // a different problem from being signed out — say so rather than showing a
  // login box that cannot work.
  if (session.error) {
    return (
      <div className="login-shell">
        <div className="login">
          <Problem error={session.error} />
        </div>
      </div>
    );
  }

  if (!session.data?.signed_in) {
    return <LoginPage />;
  }

  return (
    <div className="app">
      <Sidebar modelCount={models.data?.length} />
      <Routes>
        <Route path="/" element={<ModelsPage />} />
        <Route path="/vllm-models" element={<VllmPage />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/nodes" element={<NodesPage />} />
      </Routes>
    </div>
  );
}
